import { httpClient, baseUrl, getAuthHeaders } from 'src/pages/admin/providers/AppDataProvider';
import type {
    PerpsKpisResp,
    PerpsPerformanceResp,
    PerpsCoinDistributionResp,
    PerpsUserVolumeDistributionResp,
} from './perpetualsDashboard.types';

const BASE = `${baseUrl}/perpetuals/dashboard`;

function buildQuery(params: Record<string, any>) {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') return;
        sp.set(k, String(v));
    });
    const qs = sp.toString();
    return qs ? `?${qs}` : '';
}

async function apiGet<T>(path: string, params: Record<string, any>) {
    const url = `${path}${buildQuery(params)}`;
    const { json } = await httpClient(url, { method: 'GET' });
    return json as T;
}

export function getPerpsKpis(params: { from: number; to: number; timezone: string }) {
    return apiGet<PerpsKpisResp>(`${BASE}/kpis`, params);
}

export function getPerpsPerformance(params: { from: number; to: number; timezone: string; granularity: 'hour' | 'day' }) {
    return apiGet<PerpsPerformanceResp>(`${BASE}/performance`, params);
}

export function getPerpsCoinDistribution(params: { from: number; to: number; timezone: string; limit?: number }) {
    return apiGet<PerpsCoinDistributionResp>(`${BASE}/coin-distribution`, params);
}

export function getPerpsUserVolumeDistribution(params: { from: number; to: number; timezone: string }) {
    return apiGet<PerpsUserVolumeDistributionResp>(`${BASE}/user-volume-distribution`, params);
}

function parseFilenameFromDisposition(disposition: string | null) {
    if (!disposition) return null;
    const m = disposition.match(/filename\s*=\s*"([^"]+)"/i);
    return m?.[1] ?? null;
}

export async function exportPerpsDashboardCsv(params: {
    from: number; // ms
    to: number;   // ms
    timezone: string;
    maxDays?: number;
}) {
    const url = `${BASE}/export${buildQuery(params)}`;
    const headers = await getAuthHeaders({ Accept: 'text/csv,*/*' });

    const res = await fetch(url, {
        method: 'GET',
        headers,
    });

    if (!res.ok) {
        let msg = `Export failed (${res.status})`;
        try {
            const ct = res.headers.get('content-type') || '';
            if (ct.includes('application/json')) {
                const j = await res.json();
                msg = j?.message ?? msg;
            } else {
                const t = await res.text();
                if (t) msg = t;
            }
        } catch (e) {
            // ignore
        }
        throw new Error(msg);
    }

    const blob = await res.blob();
    const filename =
        parseFilenameFromDisposition(res.headers.get('content-disposition')) ||
        `perps-dashboard-${params.timezone}-${Date.now()}.csv`;

    return { blob, filename };
}
